<template>
	<div class="project-picker">
		<div class="label">{{ t("project-switcher.active-project") }}</div>

		<Popover.Root v-model:open="isOpen" :positioning="{ placement: 'bottom-start', gutter: 4, sameWidth: true }">
			<Popover.Trigger as-child>
				<button type="button" class="trigger">
					<AppIcon :name="IconName.Folder" class="icon" />
					<span class="name">{{ activeProjectName }}</span>
					<AppIcon :name="IconName.ChevronDown" class="chevron" :class="{ open: isOpen }" />
				</button>
			</Popover.Trigger>

			<Popover.Positioner>
				<Popover.Content class="popover">
					<button type="button" class="view-all" @click="viewAllProjects">
						<AppIcon :name="IconName.Grid" class="icon" />
						<span class="label">{{ t("project-switcher.view-all-projects") }}</span>
						<AppIcon :name="IconName.ChevronRight" class="chevron" />
					</button>

					<div class="separator"></div>

					<div class="label">{{ t("project-switcher.switch-project") }}</div>

					<div class="search">
						<div class="field">
							<AppIcon :name="IconName.Search" class="icon" />
							<input
								v-model="searchQuery"
								type="text"
								:placeholder="t('project-switcher.search-placeholder')"
								@click.stop
							/>
						</div>
					</div>

					<div class="list">
						<button
							v-for="project in filteredProjects"
							:key="project.id"
							type="button"
							class="item"
							:class="{ active: project.id === currentProject?.id }"
							@click="selectProject(project)"
						>
							<span class="name">{{ displayName(project) }}</span>
						</button>
						<p v-if="filteredProjects.length === 0" class="empty">{{ t("project-switcher.no-results") }}</p>
					</div>
				</Popover.Content>
			</Popover.Positioner>
		</Popover.Root>

		<Button
			v-if="canUpdateProjectSearchContext"
			variant="primary"
			size="s"
			class="update-search-context"
			:disabled="isUpdatingProjectSearchContext"
			@click="updateProjectSearchContext"
		>
			{{
				isUpdatingProjectSearchContext ? t("project-switcher.updating-project") : t("project-switcher.update-project")
			}}
		</Button>
	</div>
</template>

<script setup lang="ts">
import { Popover } from "@ark-ui/vue"
import AppIcon from "@/components/AppIcon.vue"
import Button from "@/components/ui/Button.vue"
import { IconName } from "@/components/icons/registry"
import { computed, inject, onMounted, ref } from "vue"
import { storeToRefs } from "pinia"
import { useI18n } from "vue-i18n"
import { useRouter } from "vue-router"
import { ProjectType } from "@/graphql"
import { Permission, UseRootStore } from "@/model/RootStore"
import { ProjectStoreKey } from "@/stores/projectStore"
import { useSearchContextStore } from "@/stores/searchContextStore"
import { projectDisplayName } from "@/utils/project"
import type { CurrentUserProject } from "@/utils/ApiClient"

const { t } = useI18n()
const router = useRouter()

const store = UseRootStore()
const { userProjects } = storeToRefs(store)

const projectStore = inject(ProjectStoreKey)!()
const { currentProject } = storeToRefs(projectStore)

const searchContextStore = useSearchContextStore()
const { isProjectSearchContextDirty } = storeToRefs(searchContextStore)

const isOpen = ref(false)
const searchQuery = ref("")
const isUpdatingProjectSearchContext = ref(false)

onMounted(() => {
	if (!store.CurrentUserDetails) store.fetchCurrentUserDetails()
})

const activeProjectName = computed(() => {
	if (!currentProject.value) return t("project.my-workspace")
	return projectDisplayName(currentProject.value, t)
})

const filteredProjects = computed(() => {
	const query = searchQuery.value.trim().toLowerCase()
	const matches = query
		? userProjects.value.filter((project: CurrentUserProject) => displayName(project).toLowerCase().includes(query))
		: userProjects.value

	const rank = (project: CurrentUserProject) => (project.type === ProjectType.Workspace ? 0 : 1)
	return matches.slice().sort((a: CurrentUserProject, b: CurrentUserProject) => rank(a) - rank(b))
})

const canManageProjects = computed(() => store.currentUser?.has(Permission.ProjectWrite))

const canUpdateProjectSearchContext = computed(() => {
	const project = currentProject.value
	if (project == null) return false
	// the Workspace's layers/filters are saved to localStorage, not the project's search context
	if (project.type === ProjectType.Workspace) return false
	if (canManageProjects.value !== true) return false
	return isProjectSearchContextDirty.value
})

function displayName(project: CurrentUserProject): string {
	return projectDisplayName(project, t)
}

function selectProject(project: CurrentUserProject): void {
	isOpen.value = false
	if (project.id === currentProject.value?.id) return
	projectStore.switchProject(project.id)
}

function viewAllProjects(): void {
	isOpen.value = false
	router.push({ name: "hunting" })
}

async function updateProjectSearchContext(): Promise<void> {
	const project = currentProject.value
	if (project == null) return

	isUpdatingProjectSearchContext.value = true
	try {
		await projectStore.updateProjectSearchContext(project.id, project.name)
	} catch (error) {
		console.error("Error updating project search context:", error)
		alert(`Error updating project search context: ${error instanceof Error ? error.message : String(error)}`)
	} finally {
		isUpdatingProjectSearchContext.value = false
	}
}
</script>

<style scoped>
.project-picker {
	width: 100%;

	.label {
		padding: var(--spacing-xs) 0 var(--spacing-sm) var(--spacing-xs);
		color: var(--neutral-600);
		font-family: var(--font-family-primary);
		font-size: var(--font-size-xxs);
		font-weight: var(--font-weight-medium);
		text-transform: uppercase;
		letter-spacing: 0.02em;
	}

	.trigger {
		width: 100%;
		display: flex;
		align-items: center;
		gap: var(--spacing-sm);
		padding: var(--spacing-sm) var(--spacing-md);
		border: var(--border-width-thin) solid var(--neutral-200);
		border-radius: var(--border-radius-lg);
		background-color: var(--neutral-0);
		cursor: pointer;
		text-align: left;
		transition: border-color 0.2s ease;

		&:hover {
			border-color: var(--neutral-400);
		}

		.icon {
			flex-shrink: 0;
			width: var(--size-icon-lg);
			height: var(--size-icon-lg);
			color: var(--primary-green);
		}

		.name {
			flex: 1;
			min-width: 0;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
			color: var(--neutral-900);
			font-family: var(--font-family-primary);
			font-size: var(--font-size-regular);
			font-weight: var(--font-weight-medium);
		}

		.chevron {
			flex-shrink: 0;
			width: var(--size-icon-md);
			height: var(--size-icon-md);
			color: var(--neutral-600);
			transition: transform 0.2s ease;

			&.open {
				transform: rotate(180deg);
			}
		}
	}

	.update-search-context {
		width: 100%;
		min-width: 0;
		margin-top: var(--spacing-sm);
	}
}

.popover {
	width: 100%;
	min-width: var(--width-small-input);
	display: flex;
	flex-direction: column;
	background-color: var(--neutral-0);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-lg);
	box-shadow: var(--shadow-md);
	overflow: hidden;
	z-index: 1000;
	padding: var(--spacing-xs);

	.view-all {
		display: flex;
		align-items: center;
		gap: var(--spacing-sm);
		width: 100%;
		padding: var(--spacing-sm);
		border: none;
		border-radius: var(--border-radius-base);
		background: transparent;
		cursor: pointer;
		color: var(--neutral-900);
		font-family: var(--font-family-primary);
		font-size: var(--font-size-regular);
		font-weight: var(--font-weight-medium);

		&:hover {
			background-color: var(--neutral-50);
		}

		.icon,
		.chevron {
			flex-shrink: 0;
			width: var(--size-icon-md);
			height: var(--size-icon-md);
			color: var(--neutral-600);
		}

		.label {
			flex: 1;
			padding: 0;
			text-align: left;
			text-transform: none;
			font-size: var(--font-size-regular);
			letter-spacing: normal;
		}
	}

	.separator {
		height: var(--border-width-thin);
		margin: var(--spacing-xxs) var(--spacing-sm);
		background-color: var(--neutral-200);
	}

	.label {
		padding: var(--spacing-sm);
	}

	.search {
		padding: var(--spacing-xxs) var(--spacing-sm) var(--spacing-sm);

		.field {
			position: relative;
			display: flex;
			align-items: center;
		}

		.icon {
			position: absolute;
			top: 50%;
			left: calc(var(--spacing-sm) + var(--spacing-xs));
			transform: translateY(-50%);
			width: var(--size-icon-sm);
			height: var(--size-icon-sm);
			color: var(--neutral-600);
			pointer-events: none;
		}

		input {
			width: 100%;
			padding: var(--spacing-sm) var(--spacing-sm) var(--spacing-sm) var(--spacing-xl);
			border: var(--border-width-thin) solid var(--neutral-200);
			border-radius: var(--border-radius-base);
			background-color: var(--neutral-50);
			font-size: var(--font-size-regular);
			outline: none;

			&:focus {
				border-color: var(--neutral-400);
				background-color: var(--neutral-0);
			}
		}
	}

	.list {
		max-height: 260px;
		overflow-y: auto;
		display: flex;
		flex-direction: column;
		gap: var(--spacing-xxs);

		.item {
			display: flex;
			align-items: center;
			width: 100%;
			padding: var(--spacing-sm);
			border: none;
			border-radius: var(--border-radius-base);
			background: transparent;
			cursor: pointer;
			text-align: left;
			color: var(--neutral-900);
			font-family: var(--font-family-primary);
			font-size: var(--font-size-regular);

			&:hover {
				background-color: var(--neutral-100);
			}

			&.active {
				background-color: var(--neutral-50);
				font-weight: var(--font-weight-medium);
			}

			.name {
				flex: 1;
				min-width: 0;
				overflow: hidden;
				text-overflow: ellipsis;
				white-space: nowrap;
			}
		}

		.empty {
			padding: var(--spacing-sm);
			text-align: center;
			color: var(--neutral-600);
			font-style: italic;
		}
	}
}
</style>
